import { Component, OnInit, inject, signal, computed, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import { CompetitionService } from '../../core/http/competition.service';
import {
  Club,
  StandingsRow,
  GroupStandingItem,
  CompetitionMatchdayResponse,
  BackendFixture,
  SponsorContract,
  NextMatchResponse,
  ClubShieldConfig,
} from '../../core/models/types';
import { calculateAttendance, calculateMatchdayRevenues } from '../../core/simulation/simulation';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { ClubJerseyComponent } from '../../shared/ui/club-jersey/club-jersey.component';
import { ClubShieldComponent } from '../../shared/ui/club-shield/club-shield.component';
import { CompetitionComponent, DisplayFixture } from '../competitions/competition.component';
import { stadiumImage } from './stadium-image';
import { resolveVenueStadium } from './stadium-venue';

export interface ClubDisplayInfo {
  id: string;
  name: string;
  code: string;
  color: string;
  isUser: boolean;
  shield?: ClubShieldConfig | null;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, IconComponent, ClubJerseyComponent, CompetitionComponent, ClubShieldComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  readonly game = inject(GameService);
  private readonly competitionService = inject(CompetitionService);
  private readonly router = inject(Router);

  // Reference to embedded calendar component to open match inspection modal
  readonly calendarComp = viewChild<CompetitionComponent>('calendarComp');

  // Live state from backend endpoints
  readonly nextMatch = signal<NextMatchResponse | null>(null);
  readonly liveCalendar = signal<CompetitionMatchdayResponse | null>(null);
  readonly isLoadingFixture = signal<boolean>(true);
  readonly fixtureError = signal<string | null>(null);

  readonly isInitialContextLoading = signal<boolean>(true);
  readonly isLoadingStadiumKpi = signal<boolean>(true);
  readonly isLoadingTicketsKpi = signal<boolean>(true);
  readonly isLoadingSponsorsKpi = signal<boolean>(true);
  readonly isLoadingLineup = signal(true);
  readonly isLineupAvailable = signal(false);

  readonly liveStandings = signal<StandingsRow[]>([]);
  readonly isLoadingStandings = signal<boolean>(true);
  readonly standingsError = signal<string | null>(null);

  readonly currentTime = signal<number>(Date.now());
  private clockTimer: any = null;

  readonly activeSeasonId = signal<string>('');
  readonly activeClubId = signal<string>('');
  readonly activeGroupId = signal<string>('');

  ngOnDestroy(): void {
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }

  async ngOnInit(): Promise<void> {
    this.isInitialContextLoading.set(true);
    this.isLoadingFixture.set(true);
    this.isLoadingStandings.set(true);
    this.isLoadingStadiumKpi.set(true);
    this.isLoadingTicketsKpi.set(true);
    this.isLoadingSponsorsKpi.set(true);
    this.isLoadingLineup.set(true);
    this.isLineupAvailable.set(false);
    this.fixtureError.set(null);
    this.standingsError.set(null);

    try {
      // 1. Resolver autoritativamente el contexto de competición desde el backend
      const context = await this.competitionService.resolveCurrentCompetitionContext();

      if (context.status !== 'JOINED' || !context.clubId) {
        // El usuario no tiene club o está en estado NOT_JOINED: no inventar datos ni llamar endpoints falsos
        this.isLoadingFixture.set(false);
        this.isLoadingStandings.set(false);
        this.isInitialContextLoading.set(false);
        this.isLoadingStadiumKpi.set(false);
        this.isLoadingTicketsKpi.set(false);
        this.isLoadingSponsorsKpi.set(false);
        this.router.navigate(['/leagues']);
        return;
      }

      this.activeSeasonId.set(context.seasonId || '');
      this.activeClubId.set(context.clubId);
      this.activeGroupId.set(context.clubId || '');

      // Sincronizar el estado del juego con la respuesta autoritativa
      const compState = await this.competitionService.getResolvedCompetitionState();
      if (compState) {
        this.game.syncWithCompetitions(compState);
      }
      this.clockTimer = setInterval(() => this.currentTime.set(Date.now()), 1000);

      const effectiveClubId = context.clubId || this.game.activeUserClubId() || this.game.userClub()?.id;

      // 2. Solicitar datos oficiales de estadio, entradas, contratos de sponsors, próximo partido, clasificación en paralelo
      await Promise.all([
        this.game.fetchClubLineup(context.clubId, true)
          .then(lineup => this.isLineupAvailable.set(lineup !== null))
          .catch(() => this.isLineupAvailable.set(false))
          .finally(() => this.isLoadingLineup.set(false)),
        effectiveClubId
          ? this.game.fetchClubStadium(effectiveClubId, false)
              .catch((e) => console.warn('Error fetching stadium on dashboard:', e))
              .finally(() => this.isLoadingStadiumKpi.set(false))
          : Promise.resolve().then(() => this.isLoadingStadiumKpi.set(false)),
        effectiveClubId
          ? this.game.fetchClubTicketPrice(effectiveClubId, false)
              .catch((e) => console.warn('Error fetching ticket price on dashboard:', e))
              .finally(() => this.isLoadingTicketsKpi.set(false))
          : Promise.resolve().then(() => this.isLoadingTicketsKpi.set(false)),
        effectiveClubId
          ? this.game.fetchSponsorContracts(effectiveClubId, false)
              .catch((e) => console.warn('Error fetching sponsor contracts on dashboard:', e))
              .finally(() => this.isLoadingSponsorsKpi.set(false))
          : Promise.resolve().then(() => this.isLoadingSponsorsKpi.set(false)),
        this.loadNextMatch(),
        this.loadNextMatchCalendar(context.seasonId, context.clubId),
        context.clubId
          ? this.loadStandings(context.clubId)
          : Promise.resolve(),
      ]);
      this.isInitialContextLoading.set(false);
    } catch (err: any) {
      const msg = err?.message || 'Error al inicializar el contexto de competición.';
      this.fixtureError.set(msg);
      this.standingsError.set(msg);
      this.isLoadingFixture.set(false);
      this.isLoadingStandings.set(false);
      this.isInitialContextLoading.set(false);
      this.isLoadingStadiumKpi.set(false);
      this.isLoadingTicketsKpi.set(false);
      this.isLoadingSponsorsKpi.set(false);
    }
  }

  /**
   * Carga el próximo encuentro oficial desde el endpoint específico:
   * GET /api/me/competitions/next-match
   */
  async loadNextMatch(): Promise<void> {
    this.isLoadingFixture.set(true);
    this.fixtureError.set(null);

    try {
      const match = await this.competitionService.getNextMatch();
      if (match) {
        this.nextMatch.set(match);
        if (match.seasonId && !this.activeSeasonId()) {
          this.activeSeasonId.set(match.seasonId);
        }

      }
    } catch (err: any) {
      console.warn('Error loading /api/me/competitions/next-match, attempting calendar fallback:', err);
      const sId = this.activeSeasonId();
      const cId = this.activeClubId();
      if (sId && cId) {
        await this.loadNextMatchCalendar(sId, cId);
      } else {
        this.fixtureError.set(err?.message || 'Error al recuperar el próximo encuentro.');
      }
    } finally {
      this.isLoadingFixture.set(false);
    }
  }

  /**
   * Carga la jornada actual y el próximo encuentro oficial desde el endpoint:
   * GET /api/competitions/seasons/{seasonId}/clubs/{clubId}/calendar
   */
  async loadNextMatchCalendar(seasonId?: string, clubId?: string): Promise<void> {
    const sId = seasonId || this.activeSeasonId();
    const cId = clubId || this.activeClubId();
    if (!sId || !cId) {
      this.isLoadingFixture.set(false);
      return;
    }

    this.isLoadingFixture.set(true);
    this.fixtureError.set(null);

    try {
      this.activeSeasonId.set(sId);
      this.activeClubId.set(cId);

      const calendar = await this.competitionService.getClubCalendar(sId, cId);
      if (calendar) {
        this.liveCalendar.set(calendar);

      }
    } catch (err: any) {
      this.fixtureError.set(err?.message || 'Error al recuperar el próximo encuentro.');
    } finally {
      this.isLoadingFixture.set(false);
    }
  }

  /**
   * Carga la clasificación en vivo del grupo de competición desde el endpoint:
   * GET /api/groups/{competitionGroupId}/standings
   */
  async loadStandings(groupId?: string, forceRefresh = false): Promise<void> {
    const gId = groupId || this.activeGroupId();
    if (!gId) {
      this.isLoadingStandings.set(false);
      return;
    }

    this.isLoadingStandings.set(true);
    this.standingsError.set(null);
    this.activeGroupId.set(gId);

    try {
      const response: any = await this.competitionService.getGroupStandings(gId, forceRefresh);
      const rawList: any[] = Array.isArray(response)
        ? response
        : response?.standings ||
          response?.items ||
          response?.data ||
          response?.groupStandings ||
          response?.rows ||
          [];

      if (rawList.length > 0) {
        const rows: StandingsRow[] = rawList.map((item: any, idx: number) => {
          const clubId = item.clubId || item.id || item.club?.id || `club-${idx}`;
          const clubName = item.clubName || item.name || item.club?.name || `Club ${idx + 1}`;

          const club = this.game.clubs().find(
            (c) =>
              c.id === clubId ||
              (c.name && clubName && c.name.toLowerCase() === clubName.toLowerCase())
          );

          const rawCode = item.clubCode || item.code || item.club?.code || club?.code;
          const clubCode =
            rawCode && rawCode.length <= 4 && !rawCode.includes('-')
              ? rawCode.toUpperCase()
              : this.getBadgeCode({ id: clubId, name: clubName, code: rawCode });

          return {
            clubId,
            clubName,
            clubCode,
            played: item.played ?? 0,
            sportingMatchesPlayed: item.sportingMatchesPlayed, administrativeLosses: item.administrativeLosses, byes: item.byes,
            won: item.won ?? 0,
            drawn: item.drawn ?? 0,
            lost: item.lost ?? 0,
            goalsFor: item.goalsFor ?? 0,
            goalsAgainst: item.goalsAgainst ?? 0,
            goalDifference:
              item.goalDifference !== undefined
                ? item.goalDifference
                : (item.goalsFor ?? 0) - (item.goalsAgainst ?? 0),
            points: item.points ?? 0,
            recentForm: item.recentForm || [],
            position: item.position ?? idx + 1,
          };
        });

        // Preserve the authoritative order and shared positions from the server.

        this.liveStandings.set(rows);
        this.game.setStandings(rows);
      }
    } catch (err: any) {
      this.standingsError.set(err?.message || 'Error al recuperar la clasificación del grupo.');
    } finally {
      this.isLoadingStandings.set(false);
    }
  }

  // Enlace computado al partido de este club desde el backend calendar
  readonly backendFixture = computed<BackendFixture | null>(() => {
    const cal = this.liveCalendar();
    if (!cal || !cal.fixtures || cal.fixtures.length === 0) return null;

    const targetId = cal.club?.id || this.activeClubId() || this.game.userClub().id;
    const targetName = (cal.club?.name || this.game.userClub().name || '').toLowerCase();
    const targetCode = (cal.club?.code || this.game.userClub().code || '').toLowerCase();

    // Resolve IDs across the whole round before using legacy display metadata.
    // Private league calendars can legitimately return null club codes.
    const byId = cal.fixtures.find(
      (f) => f.homeClub.id === targetId || f.awayClub.id === targetId
    );
    if (byId) return byId;

    const found = cal.fixtures.find(
      (f) =>
        (targetName.length > 0 && (f.homeClub.name?.toLowerCase() === targetName ||
          f.awayClub.name?.toLowerCase() === targetName)) ||
        (targetCode.length > 0 && (f.homeClub.code?.toLowerCase() === targetCode ||
          f.awayClub.code?.toLowerCase() === targetCode))
    );

    return found || cal.fixtures[0] || null;
  });

  // Fallback si todavía no ha cargado el backend calendar
  private fallbackFixture() {
    const userC = this.game.userClub();
    const md = this.game.currentMatchday();
    return md?.fixtures.find((f) => f.homeClubId === userC.id || f.awayClubId === userC.id);
  }

  readonly currentMatchdayNumber = computed<number>(() => {
    return this.nextMatch()?.matchdayNumber ?? this.liveCalendar()?.matchdayNumber ?? this.game.currentMatchdayNumber();
  });

  readonly isMatchdayStarted = computed<boolean>(() => {
    const nm = this.nextMatch() ?? this.liveCalendar();
    if (!nm || !nm.startsAt) return false;
    const startMs = new Date(nm.startsAt).getTime();
    if (isNaN(startMs)) return false;
    return this.currentTime() >= startMs;
  });

  readonly matchdayCountdown = computed<string>(() => {
    const nm = this.nextMatch() ?? this.liveCalendar();
    if (!nm || !nm.startsAt) return '';
    const startMs = new Date(nm.startsAt).getTime();
    if (isNaN(startMs)) return '';
    const diff = startMs - this.currentTime();
    if (diff <= 0) return 'Comenzando...';

    return this.formatCountdown(diff);
  });

  private formatCountdown(milliseconds: number): string {
    const totalSeconds = Math.floor(Math.max(0, milliseconds) / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  }

  readonly matchdayProgress = computed<{ progress: number; remaining: string; startsAtFormatted: string; endsAtFormatted: string } | null>(() => {
    const nm = this.nextMatch() ?? this.liveCalendar();
    if (!nm || !nm.startsAt || !nm.endsAt) return null;

    const startMs = new Date(nm.startsAt).getTime();
    const endMs = new Date(nm.endsAt).getTime();
    const nowMs = this.currentTime();

    if (isNaN(startMs) || isNaN(endMs) || endMs <= startMs || nowMs < startMs) return null;

    const total = endMs - startMs;
    const elapsed = nowMs - startMs;
    const rawPct = Math.max(0, Math.min(100, (elapsed / total) * 100));

    const formatDate = (iso: string) => {
      try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      } catch {
        return '';
      }
    };

    return {
      progress: Math.round(rawPct * 10) / 10,
      remaining: this.formatCountdown(endMs - nowMs),
      startsAtFormatted: formatDate(nm.startsAt),
      endsAtFormatted: formatDate(nm.endsAt),
    };
  });

  readonly totalMatchdays = computed<number>(() => {
    return this.liveCalendar()?.totalMatchdays ?? 38;
  });

  readonly calendarGroupName = computed<string>(() => {
    return this.nextMatch()?.groupName || this.liveCalendar()?.groupName || '';
  });

  readonly isPlayoff = computed<boolean>(() => {
    const phase = this.nextMatch()?.phase || this.liveCalendar()?.phase;
    return phase?.toUpperCase() === 'PLAYOFF';
  });

  readonly playoffCycleNumber = computed<number | null>(() => {
    return this.nextMatch()?.cycleNumber ?? this.liveCalendar()?.cycleNumber ?? null;
  });

  readonly playoffType = computed<string | null>(() => {
    return this.nextMatch()?.playoffType ?? this.liveCalendar()?.playoffType ?? null;
  });

  readonly playoffTypeLabel = computed<string>(() => {
    const pt = this.playoffType()?.toUpperCase();
    if (pt === 'MAIN') return 'Principal';
    if (pt === 'PROMOTION') return 'Ascenso';
    if (pt === 'RELEGATION') return 'Permanencia';
    return this.playoffType() || '';
  });

  readonly playoffHeading = computed<string>(() => {
    const typeLabel = this.playoffTypeLabel();
    if (typeLabel) {
      return `Fase de Playoff (${typeLabel})`;
    }
    return 'Fase de Playoff';
  });

  readonly playoffSubtext = computed<string>(() => {
    const cycle = this.playoffCycleNumber();
    if (cycle) {
      return `Eliminatoria decisiva del ciclo ${cycle}. Todo en juego por el ascenso de categoría.`;
    }
    return 'Eliminatoria decisiva. Todo en juego en esta ronda eliminatoria.';
  });

  readonly matchStartsAtFormatted = computed<string | null>(() => {
    const raw = this.nextMatch()?.startsAt || this.liveCalendar()?.startsAt;
    if (!raw) return null;
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  });

  readonly venueStadium = computed(() => {
    const match = this.nextMatch();
    // Never read our club to fill in missing opponent stadium information.
    if (match) return resolveVenueStadium(match, null, false);
    if (!this.isHome()) return null;
    const data = this.game.stadiumData();
    const own = this.game.userClub().stadium;
    return resolveVenueStadium(null, data ? {
      name: data.stadiumName || data.name || own.name,
      capacity: data.capacity,
      ratingLevel: data.ratingLevel,
      facilitiesRatingLevel: data.facilitiesRatingLevel,
    } : own, true);
  });

  readonly effectiveStadiumName = computed(() => this.venueStadium()?.name ?? 'Estadio no disponible');
  readonly effectiveStadiumCapacity = computed(() => this.venueStadium()?.capacity ?? 0);
  readonly effectiveStadiumRating = computed(() => this.venueStadium()?.ratingLevel ?? 0);

  readonly effectiveStadiumRatingFormatted = computed<string>(() => {
    const r = this.effectiveStadiumRating();
    return r % 1 === 0 ? r.toFixed(0) : r.toFixed(1);
  });

  readonly stadiumStarsArray = [1, 2, 3, 4, 5];

  readonly effectiveStadiumImage = computed<string | null>(() => {
    const stadium = this.venueStadium();
    return stadium ? stadiumImage(stadium.facilitiesRatingLevel, stadium.capacity) : null;
  });

  readonly effectiveStadiumCategory = computed<string>(() => {
    const rating = this.venueStadium()?.facilitiesRatingLevel ?? 0;
    if (rating >= 5.0) return 'Instalaciones de élite';
    if (rating >= 4.5) return 'Instalaciones premium';
    if (rating >= 4.0) return 'Instalaciones modernas';
    if (rating >= 3.5) return 'Instalaciones avanzadas';
    if (rating >= 3.0) return 'Instalaciones profesionales';
    if (rating >= 2.5) return 'Instalaciones renovadas';
    if (rating >= 2.0) return 'Instalaciones sencillas';
    if (rating >= 1.5) return 'Instalaciones básicas mejoradas';
    return 'Instalaciones básicas';
  });

  readonly isHome = computed<boolean>(() => {
    const nm = this.nextMatch();
    if (nm && nm.venue) {
      return nm.venue.toUpperCase() === 'HOME';
    }
    const bf = this.backendFixture();
    if (bf) {
      const targetId = this.liveCalendar()?.club?.id || this.activeClubId() || this.game.userClub().id;
      if (bf.homeClub.id === targetId) return true;
      if (bf.awayClub.id === targetId) return false;
      const targetName = (this.liveCalendar()?.club?.name || this.game.userClub().name || '').toLowerCase();
      return (
        targetName.length > 0 && bf.homeClub.name?.toLowerCase() === targetName
      );
    }
    const userC = this.game.userClub();
    const ff = this.fallbackFixture();
    return ff?.homeClubId === userC.id;
  });

  private getStoredShield(clubId?: string): ClubShieldConfig | null {
    if (!clubId || typeof localStorage === 'undefined') return null;
    try {
      const saved = localStorage.getItem(`lom_club_shield_${clubId}`);
      if (saved) return JSON.parse(saved);
    } catch {}
    return null;
  }

  readonly homeClubDisplay = computed<ClubDisplayInfo>(() => {
    const nm = this.nextMatch();
    if (nm && nm.homeClub) {
      const isUser = this.isHome();
      const s = isUser ? (this.game.userClub()?.shield || this.getStoredShield(nm.homeClub.id)) : ((nm.homeClub as any).shield || this.getStoredShield(nm.homeClub.id));
      return {
        id: nm.homeClub.id,
        name: nm.homeClub.name,
        code: this.getBadgeCode(nm.homeClub),
        color: this.getClubBadgeColor(nm.homeClub.id, nm.homeClub.name),
        isUser,
        shield: s,
      };
    }

    const bf = this.backendFixture();
    if (bf) {
      const isUser = this.isHome();
      const s = isUser ? (this.game.userClub()?.shield || this.getStoredShield(bf.homeClub.id)) : ((bf.homeClub as any).shield || this.getStoredShield(bf.homeClub.id));
      return {
        id: bf.homeClub.id,
        name: bf.homeClub.name,
        code: this.getBadgeCode(bf.homeClub),
        color: this.getClubBadgeColor(bf.homeClub.id, bf.homeClub.name),
        isUser,
        shield: s,
      };
    }

    const ff = this.fallbackFixture();
    const home = ff ? this.game.clubs().find((c) => c.id === ff.homeClubId) : this.game.userClub();
    const isUser = home?.id === this.game.userClub().id;
    const s = isUser ? (this.game.userClub()?.shield || this.getStoredShield(home?.id)) : (home?.shield || this.getStoredShield(home?.id));
    return {
      id: home?.id || 'home',
      name: home?.name || 'Club Local',
      code: this.getBadgeCode({ id: home?.id, name: home?.name, code: home?.code }),
      color: home?.primaryColor || this.getColorForString(home?.name || 'home'),
      isUser,
      shield: s,
    };
  });

  readonly awayClubDisplay = computed<ClubDisplayInfo>(() => {
    const nm = this.nextMatch();
    if (nm && nm.awayClub) {
      const isUser = !this.isHome();
      const s = isUser ? (this.game.userClub()?.shield || this.getStoredShield(nm.awayClub.id)) : ((nm.awayClub as any).shield || this.getStoredShield(nm.awayClub.id));
      return {
        id: nm.awayClub.id,
        name: nm.awayClub.name,
        code: this.getBadgeCode(nm.awayClub),
        color: this.getClubBadgeColor(nm.awayClub.id, nm.awayClub.name),
        isUser,
        shield: s,
      };
    }

    const bf = this.backendFixture();
    if (bf) {
      const isUser = !this.isHome();
      const s = isUser ? (this.game.userClub()?.shield || this.getStoredShield(bf.awayClub.id)) : ((bf.awayClub as any).shield || this.getStoredShield(bf.awayClub.id));
      return {
        id: bf.awayClub.id,
        name: bf.awayClub.name,
        code: this.getBadgeCode(bf.awayClub),
        color: this.getClubBadgeColor(bf.awayClub.id, bf.awayClub.name),
        isUser,
        shield: s,
      };
    }

    const ff = this.fallbackFixture();
    const away = ff ? this.game.clubs().find((c) => c.id === ff.awayClubId) : this.game.clubs()[1];
    const isUser = away?.id === this.game.userClub().id;
    const s = isUser ? (this.game.userClub()?.shield || this.getStoredShield(away?.id)) : (away?.shield || this.getStoredShield(away?.id));
    return {
      id: away?.id || 'away',
      name: away?.name || 'Club Visitante',
      code: this.getBadgeCode({ id: away?.id, name: away?.name, code: away?.code }),
      color: away?.primaryColor || this.getColorForString(away?.name || 'away'),
      isUser,
      shield: s,
    };
  });

  readonly homeClubJerseySponsor = computed<SponsorContract | null>(() => {
    const nm = this.nextMatch();
    if (nm && nm.homeClub) {
      const shirt =
        nm.homeClub.sponsors?.find((s) => s.category?.toUpperCase() === 'SHIRT');
      if (shirt) {
        return {
          id: `next-match-sponsor-${nm.homeClub.id}`,
          clubId: nm.homeClub.id,
          sponsorName: shirt.name,
          logoUrl: shirt.logoUrl || null,
          type: 'SHIRT',
          status: 'ACTIVE',
          economicModality: 'FIXED',
          fixedAmount: 0,
          winBonusAmount: 0,
        } as SponsorContract;
      }
    }

    return null;
  });

  readonly awayClubJerseySponsor = computed<SponsorContract | null>(() => {
    const nm = this.nextMatch();
    if (nm && nm.awayClub) {
      const shirt =
        nm.awayClub.sponsors?.find((s) => s.category?.toUpperCase() === 'SHIRT');
      if (shirt) {
        return {
          id: `next-match-sponsor-${nm.awayClub.id}`,
          clubId: nm.awayClub.id,
          sponsorName: shirt.name,
          logoUrl: shirt.logoUrl || null,
          type: 'SHIRT',
          status: 'ACTIVE',
          economicModality: 'FIXED',
          fixedAmount: 0,
          winBonusAmount: 0,
        } as SponsorContract;
      }
    }

    return null;
  });

  readonly matchScore = computed<string | null>(() => {
    const bf = this.backendFixture();
    if (bf && bf.homeGoals !== null && bf.awayGoals !== null) {
      return `${bf.homeGoals} - ${bf.awayGoals}`;
    }
    const ff = this.fallbackFixture();
    if (ff && ff.played && ff.homeScore !== undefined && ff.awayScore !== undefined) {
      return `${ff.homeScore} - ${ff.awayScore}`;
    }
    return null;
  });

  readonly nextMatchDisplayFixture = computed<DisplayFixture | null>(() => {
    const nm = this.nextMatch();
    const bf = this.backendFixture();
    const ff = this.fallbackFixture();

    const id = nm?.fixtureId || bf?.id || ff?.id;
    if (!id) return null;

    const home = this.homeClubDisplay();
    const away = this.awayClubDisplay();

    let homeScore: number | undefined = undefined;
    let awayScore: number | undefined = undefined;

    if (bf && bf.homeGoals !== null && bf.homeGoals !== undefined) {
      homeScore = bf.homeGoals;
    } else if (ff && ff.homeScore !== undefined) {
      homeScore = ff.homeScore;
    }

    if (bf && bf.awayGoals !== null && bf.awayGoals !== undefined) {
      awayScore = bf.awayGoals;
    } else if (ff && ff.awayScore !== undefined) {
      awayScore = ff.awayScore;
    }

    const played =
      (homeScore !== undefined && awayScore !== undefined) ||
      bf?.status === 'FINISHED' ||
      ff?.played ||
      false;
    const status = nm?.status || bf?.status || (played ? 'FINISHED' : 'PLANNED');

    return {
      id,
      homeClubId: home.id,
      awayClubId: away.id,
      homeClubName: home.name,
      awayClubName: away.name,
      homeClubCode: home.code,
      awayClubCode: away.code,
      homeScore,
      awayScore,
      played,
      status,
      phase: nm?.phase || bf?.phase,
      playoffType: nm?.playoffType || bf?.playoffType,
      cycleNumber: nm?.cycleNumber ?? this.liveCalendar()?.cycleNumber,
      engineCalculation: (ff as any)?.engineCalculation,
    };
  });

  openNextMatchDetail(): void {
    const comp = this.calendarComp();
    if (!comp) return;

    const targetFixture = this.nextMatchDisplayFixture();
    if (!targetFixture) return;

    const matched = comp
      .fixtures()
      .find(
        (f) =>
          f.id === targetFixture.id ||
          (f.homeClubId === targetFixture.homeClubId && f.awayClubId === targetFixture.awayClubId)
      );

    void comp.inspectFixture(matched || targetFixture);
  }

  assignedStartersCount(): number {
    return this.game.lineupSlots().filter((s) => s.playerId !== null).length;
  }

  isLineupComplete(): boolean {
    return this.assignedStartersCount() === 11;
  }

  attendanceProj() {
    if (!this.isHome()) return null;
    const club = this.game.userClub();
    const stadium = club?.stadium || {
      name: 'Estadio Municipal',
      capacity: 15000,
      seasonTicketHolders: 10000,
      seasonTicketPrice: 260,
      ticketPrice: 35,
      modules: { parkingLevel: 1, cafeteriaLevel: 1, merchandisingLevel: 1 },
    };
    if (!this.venueStadium()) return null;
    const effectiveCapacity = this.effectiveStadiumCapacity();
    const effectiveTicketPrice = this.game.ticketPriceData()?.ticketPrice ?? stadium.ticketPrice ?? 35;
    const simulatedStadium = {
      ...stadium,
      name: this.effectiveStadiumName(),
      capacity: effectiveCapacity,
      ticketPrice: effectiveTicketPrice,
    };
    const form = club?.recentForm || ['W', 'D', 'W', 'W', 'D'];
    const att = calculateAttendance(simulatedStadium, form);
    const rev = calculateMatchdayRevenues(
      att.generalTicketsSold,
      effectiveTicketPrice,
      att.attendance,
      simulatedStadium
    );
    return {
      attendance: att.attendance,
      percentage: att.percentage,
      ticketRevenueM: rev.ticketRevenueM,
      commercialRevenueM: rev.commercialRevenueM,
    };
  }

  readonly standings = computed<StandingsRow[]>(() => {
    const live = this.liveStandings();
    if (live.length > 0) return live;
    if (!this.isLoadingStandings() && this.standingsError()) {
      return [];
    }
    return [];
  });

  getBadgeCode(club: { id?: string; name?: string; code?: string }): string {
    const rawCode = club.code;
    if (rawCode && rawCode.length <= 4 && !rawCode.includes('-')) {
      return rawCode.toUpperCase();
    }
    const name = club.name || '';
    if (name) {
      const clean = name.replace(/[^A-Za-z0-9]/g, '');
      if (clean.length > 0) {
        return clean.slice(0, 3).toUpperCase();
      }
    }
    return 'CLB';
  }

  getClubBadgeColor(clubId: string, name?: string): string {
    const club = this.game.clubs().find((c) => c.id === clubId);
    if (club?.primaryColor) return club.primaryColor;
    return this.getColorForString(name || clubId);
  }

  goToSponsors(): void {
    this.router.navigate(['/stadium'], { queryParams: { tab: 'sponsors' } });
  }

  goToStadium(): void {
    this.router.navigate(['/stadium'], { queryParams: { tab: 'stadium' } });
  }

  averageStaff(): number {
    const staff = this.game.userClub().staff;
    return (staff.goalkeeping + staff.fitness + staff.technical + staff.shooting) / 4;
  }

  private getColorForString(str: string): string {
    const PALETTE = [
      '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed',
      '#0891b2', '#4f46e5', '#db2777', '#ea580c', '#0284c7'
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return PALETTE[Math.abs(hash) % PALETTE.length];
  }
}
